import { useCallback, useContext } from 'react';

import { useAccessModal } from 'common/containers/AccessModalContainer';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import hasPermission from 'common/util/hasPermission';

import type { RolePermissionName } from 'common/api/endpoints/companies';

type PermissionKeys = RolePermissionName | RolePermissionName[];

const normalizePermissions = (permissions: PermissionKeys): RolePermissionName[] => {
  return Array.isArray(permissions) ? permissions : [permissions];
};

export const usePermissionGuard = () => {
  const company = useContext(CompanyContext);
  const viewer = useContext(ViewerContext);
  const openAccessModal = useAccessModal();

  const hasPermissions = useCallback(
    (permissions: PermissionKeys): boolean => {
      if (!company || !viewer) {
        return false;
      }

      const requiredPermissions = normalizePermissions(permissions);

      if (requiredPermissions.length === 0) {
        return true;
      }

      return requiredPermissions.every((permissionKey) =>
        hasPermission(permissionKey, company, viewer),
      );
    },
    [company, viewer],
  );

  const ensurePermissions = useCallback(
    (permissions: PermissionKeys): boolean => {
      const requiredPermissions = normalizePermissions(permissions);

      if (!hasPermissions(requiredPermissions)) {
        openAccessModal(requiredPermissions);
        return false;
      }

      return true;
    },
    [hasPermissions, openAccessModal],
  );

  const withPermission =
    <Args extends unknown[]>(
      permissions: PermissionKeys,
      handler: (...args: Args) => void | Promise<unknown>,
    ) =>
    async (...args: Args) => {
      const requiredPermissions = normalizePermissions(permissions);

      if (!hasPermissions(requiredPermissions)) {
        openAccessModal(requiredPermissions);
        return;
      }

      await handler(...args);
    };

  return {
    hasPermissions,
    ensurePermissions,
    withPermission,
  };
};
